/**
 * Offline-storage helper voor BillFac
 *
 * Verantwoordelijkheden:
 *  - Cache klanten + artikels in IndexedDB voor offline-toegang
 *  - Queue POST-acties (orders, bezoeken) wanneer offline
 *  - Sync de queue automatisch zodra terug online
 *  - Online/offline-status emit naar listeners
 */
import { openDB } from 'idb';

const DB_NAME = 'billfac-offline';
const DB_VERSION = 1;

const STORES = {
  KLANTEN: 'klanten',
  ARTIKELS: 'artikels',
  QUEUE: 'pending_queue',
  META: 'meta',
};

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORES.KLANTEN)) db.createObjectStore(STORES.KLANTEN, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(STORES.ARTIKELS)) db.createObjectStore(STORES.ARTIKELS, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(STORES.QUEUE)) {
          const queueStore = db.createObjectStore(STORES.QUEUE, { keyPath: 'id', autoIncrement: true });
          queueStore.createIndex('status', 'status');
          queueStore.createIndex('createdAt', 'createdAt');
        }
        if (!db.objectStoreNames.contains(STORES.META)) db.createObjectStore(STORES.META, { keyPath: 'key' });
      },
    });
  }
  return dbPromise;
}

// ─── KLANTEN / ARTIKELS cache ──────────────────────────────────
export async function cacheKlanten(klanten) {
  const db = await getDB();
  const tx = db.transaction(STORES.KLANTEN, 'readwrite');
  await tx.store.clear();
  for (const k of klanten || []) {
    if (k.id) await tx.store.put(k);
  }
  await tx.done;
  await setMeta('klanten_synced_at', new Date().toISOString());
  return (klanten || []).length;
}

export async function getCachedKlanten() {
  const db = await getDB();
  return db.getAll(STORES.KLANTEN);
}

export async function cacheArtikels(artikels) {
  const db = await getDB();
  const tx = db.transaction(STORES.ARTIKELS, 'readwrite');
  await tx.store.clear();
  for (const a of artikels || []) {
    if (a.id) await tx.store.put(a);
  }
  await tx.done;
  await setMeta('artikels_synced_at', new Date().toISOString());
  return (artikels || []).length;
}

export async function getCachedArtikels() {
  const db = await getDB();
  return db.getAll(STORES.ARTIKELS);
}

// ─── META ─────────────────────────────────────────────────────
export async function setMeta(key, value) {
  const db = await getDB();
  await db.put(STORES.META, { key, value });
}

export async function getMeta(key) {
  const db = await getDB();
  const row = await db.get(STORES.META, key);
  return row?.value;
}

// ─── QUEUE ────────────────────────────────────────────────────
/**
 * Voeg een actie toe aan de offline-queue.
 * @param {object} action - {method, url, body, label, kind, klantId?}
 * @returns {Promise<number>} - lokale queue-id
 */
export async function enqueueAction(action) {
  const db = await getDB();
  const id = await db.add(STORES.QUEUE, {
    method: action.method || 'POST',
    url: action.url,
    body: action.body || null,
    label: action.label || '',
    kind: action.kind || 'order',
    klantId: action.klantId || null,
    status: 'pending',
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
  });
  notifyQueueChange();
  return id;
}

export async function getQueue() {
  const db = await getDB();
  return db.getAll(STORES.QUEUE);
}

export async function getPendingCount() {
  const db = await getDB();
  const idx = db.transaction(STORES.QUEUE).store.index('status');
  return idx.count('pending');
}

export async function deleteQueueItem(id) {
  const db = await getDB();
  await db.delete(STORES.QUEUE, id);
  notifyQueueChange();
}

export async function markQueueItem(id, patch) {
  const db = await getDB();
  const item = await db.get(STORES.QUEUE, id);
  if (!item) return;
  Object.assign(item, patch);
  await db.put(STORES.QUEUE, item);
  notifyQueueChange();
}

// ─── Listeners ─────────────────────────────────────────────────
const queueListeners = new Set();
export function onQueueChange(fn) {
  queueListeners.add(fn);
  return () => queueListeners.delete(fn);
}
function notifyQueueChange() {
  for (const fn of queueListeners) {
    try { fn(); } catch (_e) { /* niks */ }
  }
}

// ─── Sync ─────────────────────────────────────────────────────
let syncing = false;
/**
 * Flush de queue: probeer elke pending actie te verzenden.
 * Returns {sent, failed}.
 */
export async function flushQueue(apiClient) {
  if (syncing) return { sent: 0, failed: 0, skipped: true };
  if (!navigator.onLine) return { sent: 0, failed: 0, offline: true };
  syncing = true;
  let sent = 0, failed = 0;
  try {
    const queue = await getQueue();
    const pending = queue.filter(x => x.status === 'pending');
    for (const item of pending) {
      try {
        const method = (item.method || 'POST').toLowerCase();
        const config = { method, url: item.url };
        if (item.body) config.data = item.body;
        await apiClient.request(config);
        await deleteQueueItem(item.id);
        sent++;
      } catch (e) {
        const errMsg = e?.response?.data?.detail || e?.message || 'Onbekende fout';
        await markQueueItem(item.id, {
          attempts: (item.attempts || 0) + 1,
          lastError: errMsg,
          status: (item.attempts || 0) + 1 >= 5 ? 'failed' : 'pending',
        });
        failed++;
      }
    }
  } finally {
    syncing = false;
  }
  return { sent, failed };
}

// ─── Online/offline detection ─────────────────────────────────
export function setupOnlineListener(onOnline, onOffline) {
  const onlineHandler = () => onOnline?.();
  const offlineHandler = () => onOffline?.();
  window.addEventListener('online', onlineHandler);
  window.addEventListener('offline', offlineHandler);
  return () => {
    window.removeEventListener('online', onlineHandler);
    window.removeEventListener('offline', offlineHandler);
  };
}

export function isOnline() {
  return typeof navigator !== 'undefined' && navigator.onLine;
}
